import { ModuleSupplyStrip } from './ModuleSupplyStrip.tsx'
import type { SuppliesView } from './MonitoringScreen.tsx'
import { useModuleStepLog } from '@ctcells/api'
import type { ChamberRow, ModuleSnapshot, TrayCell } from '@ctcells/contract'
import { t, type MessageKey } from '@ctcells/i18n'
import { useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cx } from '../../atoms/cx.ts'
import type { StainDeckNow } from '../../figures/LineFigures.tsx'
import type { TabItem } from '../../layout/Tabs.tsx'
import { canView, type ScreenAccess } from '../access.ts'

import { equipmentBlocks, trayCorner } from './equipmentBlocks.ts'
import { FigureMetrics } from './FigureMetrics.tsx'
import { LatestStepNote } from './LatestStepNote.tsx'
import { latestStepOf, SEQUENCE_DICTIONARY } from './latestStepModel.ts'
import {
  FIGURE_METRIC_BLOCKS,
  FIGURE_METRIC_EXTRA,
  figureMetricTiles,
} from './figureMetricsModel.ts'
import { FocusFigure, type OverviewParts } from './FocusFigure.tsx'
import { METRIC_SPECS } from './metricSpecs.ts'
import { MonitoringPanel } from './MonitoringPanel.tsx'
import { ProcessSteps } from './ProcessSteps.tsx'
import { asmStationView } from './asmStationModel.ts'
import { currentProcessStep, PROCESS_STEPS } from './processSteps.ts'
import { RobotDetailTiles } from './RobotDetail.tsx'
import {
  consumableNumber,
  findModule,
  monitoringDeviceName,
  TRAY_LABEL_KEY,
  type MonitoringDeviceKey,
} from './monitoringModel.ts'
import {
  stainFrontArrow,
  stainFrontPhase,
  stainFrontStatus,
  stainFrontWaste,
} from './stainFrontModel.ts'
import { useAlignDeckLine } from './useAlignDeckLine.ts'
import { useFitZoom, type FitZoomShare } from './useFitZoom.ts'

/**
 * 「전체」 보기 조각 중 **스텝 활동 하나만** 켠다.
 * 🔴 모듈 바깥에 둔 것은 렌더마다 새 객체를 만들지 않기 위해서다 — `FocusFigure` 는
 *    `memo` 가 아니지만, 같은 값이 같은 참조로 가는 편이 앞으로도 안전하다.
 */
const STEP_ACTIVITY_ONLY: OverviewParts = { stepActivity: true }

/**
 * 그림 격자에서 빼는 블록 — **소모품 판(`SupplyPanel`)이 이미 그리는 랙만 담던 칸**이다(eric 2026-10-02 「장비에서.. 여기
 * 소모품 그림이랑 겹치잖아. 이거 지우라고.」 · 대상 「염색기쪽이랑.. 어셈블러쪽.」).
 * - `pip` — 염색기 「피펫 · 시약」. 걷기 전 이 화면에서는 데크 랙 다섯(tuberack1 · 2 · tiprack1 · 2 · coldrack)뿐이었다
 * - `tc` — 어셈블러 「TC 랙」. 걷기 전에는 데크 랙 `tcstock`(tc rack) 한 장뿐이었다
 * ⚠ 그 랙을 그리던 `FocusFigure` 의 `realFigures` 길(`deckRacks`)은 닿는 곳이 없어 2026-10-03 에 걷었다 — 랙 그림은 소모품 판 한 곳이다.
 * 🔴 **그림 탭 · 랙 표 · 그림 컴포넌트는 그대로다** — 장비 관점 콘솔 · 패드는 이 화면을 안 거쳐(`ModuleFigureView`) 종전대로 선다.
 * ⚠ 진행 강조가 이 둘을 가리키는 때(`TC_LOAD` · `TC_REMOVE` · `set_reagent_storage_temp` · 대상 `reagent_bay` 등)는 이 판에서
 *   켤 그림이 없다 — 단계 띠의 「처리 중」은 그대로 선다.
 */
const SUPPLY_RACK_BLOCKS: ReadonlySet<string> = new Set(['pip', 'tc'])

/**
 * 콘솔 「장비별 보기」 — **디자인팀 시안 `equipView()` 의 배치**(eric 2026-10-02 「장비별 보기에서.. 디자인팀이 한 html 로
 * 바꿔줘.. 그거 그림도 바꿔주고..」 · 「메트릭 화면도 바꿔주고.」).
 *
 * ```
 * ┌ .eqsheet__side ───────┐ ┌ .eqsheet__main ──────────────────────────────────────┐
 * │ [원심분리기 · 연결] 영상 │ │ [01 작업 전 C 트레이][02 처리 중 원심분리기][03 작업 후 A 트레이] │
 * │ 현재 메트릭(원심분리기) │ │ ┌────────┐  ┌──────────────┐  ┌────────┐            │
 * │ 원심분리기의 챔버 (표) │ │ │ C 트레이 │  │   원심 로터    │  │ A 트레이 │            │
 * │ 정보 (타일)           │ │ └────────┘  └──────────────┘  └────────┘            │
 * └───────────────────────┘ └──────────────────────────────────────────────────────┘
 *   6축 로봇을 고르면:  [6축 상세(동작 · 핸드오프 자리 · 정보)]  [같은 장비 카드 — 「처리 중 6축 로봇」 · 팔 그림 · 공정 진행 줄]
 * ```
 * 🔴 **왼쪽 상세 · 오른쪽 장비 판**(eric 2026-10-06 「왼쪽에는 영상... 메트릭, 정보.. 오른쪽에.. 위에 6축 로봇, 그 아래에
 *    트레이 처리 중.. 이거 넣어줘..」). ⚠ 종전(10-02 ~ 10-06) 「왼쪽 장비 판 · 오른쪽 상세 · 띠는 판 전체 위」는 걷었다.
 * 🔴 **6축 로봇은 장비 판 위 띠가 아니라 장비 선택 줄의 넷째 칸이다**(eric 2026-10-06 「6축 로봇... 위에다가 넣지 말고. 장비에다가.
 *    6축 로봇을 추가하고.. 6축 로봇 클릭하면 그거만 보이게 해줘.」). 원심분리기 · 어셈블러 · 염색기 판에는 띠가 없다 —
 *    `deviceKey === 'robot'` 일 때만 오른쪽 열이 6축 이송 카드 하나다(다른 장비 영상 · 트레이 그림은 안 선다).
 *    ⚠ 같은 날 오전의 「오른쪽 열 맨 위 띠(`transfer` 자리) · 띠 누르면 6축 상세」는 걷었다 — 되살리지 마라.
 *
 * 🔴 **상세 카드 맨 위에 그 장비의 비전 칸이 선다**(「○○ 상세」 제목 줄은 eric 2026-10-02 에 걷었다 — 접근 이름으로만 남는다)(eric 2026-10-02 「비전쪽에.. 각각의 모듈 영상들... 장비의 상세..
 *    (오른쪽) 내용 위에 넣어줘 … 오른쪽에. .비전.. 은 없애..」 · 시안 `.side` 의 `camCell` 자리). 고른 장비 것 하나만이다 —
 *    다른 모듈 영상은 DOM 에 없다. 모니터링 오른쪽 열 「비전」 탭은 걷었다.
 * 🔴 **칸은 영상 자료 유무와 무관하게 선다**(카메라는 장비의 구성 요소 — CLAUDE.md 「비전 탭은 항상 있다」의 취지).
 *    거는 조건은 «그 장비의 그림 탭에 `vision` 이 있는가»(`figureTabs`) 하나다 — 자료로 거르지 마라. 6축 로봇은 탭이 없어 칸이 없다.
 * 🔴 그림 격자에는 영상 블록이 없다 — 영상은 상세 카드 한 곳이다(같은 영상이 두 번 서지 않는다).
 * 🔴 **상세 카드에 소모품 수가 없다**(eric 2026-10-02 「장비쪽에.. 소모품은 항상 보이는 거라.. 장비 내용 중에 빼줘」) — 팁 · TC 랙
 *    잔량 타일(`source: 'consumables'`)은 오른쪽 열 소모품 판이 늘 말한다. 시약 보관 온도(장비 상태)는 남는다.
 * 🔴 **단계 띠의 이름은 시안 글자가 아니라 우리 값이다** — 트레이는 `TRAY_LABEL_KEY`, 장비는 `monitoringDeviceName()`(6축은 「운송 로봇」 · 셋은 `moduleLabel()`).
 *    단계 띠에 이름이 선 블록은 그림 위 이름표를 «눈에서만» 숨긴다(시안 `.eq__cap--dup`) — 스크린리더는 그대로 읽는다.
 * 🔴 **그림 격자는 「작업 전 | 장비 | 작업 후」 셋**이다. 염색기는 작업 후 트레이가 없어(§16-2) 「트레이 | 셰이커」 두 칸이고
 *    두 칸은 1 : 1 · 한 배율이다(eric 2026-10-02 「대칭이 되게」).
 *    규칙은 «끝 트레이가 없고 가운데가 둘 이상이면 마지막 블록이 셋째 칸» 하나이고 장비 이름으로 가르지 않는다.
 * 🔴 **소모품 랙 그림은 이 격자에 없다**(eric 2026-10-02 「소모품 그림이랑 겹치잖아. 이거 지우라고」 — `SUPPLY_RACK_BLOCKS`).
 *    염색기 「피펫 · 시약」(랙 다섯) · 어셈블러 「TC 랙」(tc rack) 칸째 걷었다. 랙은 오른쪽 열 소모품 판 한 곳이다.
 *    ⚠ 2026-10-02 오전 「그림의 랙 도형(데크 구성 요소)은 남는다」는 그 뒤 eric 지시로 뒤집혔다 — 되살리지 마라.
 * 🔴 **원심분리기 메트릭은 상세 카드의 영상(비전 칸) 바로 아래에 «언제나» 선다**(eric 2026-10-06 「원심분리기 하단에.. 메트릭값
 *    보이게」 → 같은 날 「현재 메트릭을... 원심분리기.. 영상 아래에다가 넣어줘야지.」 · `FIGURE_METRIC_BLOCKS`). 값이 응답에 없으면
 *    「미수신」 + 사유 줄(`FigureMetrics`). 그 블록은 패널의 메트릭 구역(`components`)에서 뺀다 — 같은 값이 두 번 서지 않는다.
 *    ⚠ 같은 날 오전의 「로터 아래 · 칸이 가로로 길면 로터 오른쪽」 배치는 걷었다 — 로터와 메트릭 사이가 크게 비었다(eric 캡처). 되살리지 마라.
 *    ⚠ 2026-10-02 의 「칸이 없으면 메트릭 구역째 없다」(상세 카드)는 원심분리기에 한해 뒤집혔다. 어셈블러 · 염색기는 종전대로다.
 * 🔴 **좁으면 접는다** — 카드 폭이 모자라면(컨테이너 질의 · 미디어 쿼리 아님) 「장비 / 작업 전 · 작업 후」 두 줄이다.
 *    그림은 칸마다 제 칸에 맞춘다(`useFitZoom` · 칸 하나에 한 번).
 * 🔴 **그림을 다시 그리지 않는다.** 한 블록이 `FocusFigure` 한 장이고 넘기는 `component` 만 다르다.
 *    겉모습(선 굵기 · 그림 배율)은 `monitoring.css` 의 `.eqsheet` 아래 규칙이 시안에 맞춘다(로터는 선 도면 `RotorLine` 자체가 시안 모양이다) — 장비 관점 콘솔 · 패드는 그대로다.
 * ⚠ 2026-09-21 ~ 10-02 에는 「장비가 판 가운데 · 트레이는 위 모서리에 작게」(eric 2026-09-29)였다. 시안 배치가 그 뒤의 지시라 걷었다.
 */
export interface EquipmentSheetProps {
  deviceKey: MonitoringDeviceKey
  modules: readonly ModuleSnapshot[]
  /** 🔴 **이미 이 장비로 좁혀진 것**이 넘어온다(`samplesAt`). 여기서 다시 거르지 않는다 */
  chambers: readonly ChamberRow[]
  /** 시료를 **못 봤을 때의 사유**. 챔버 표의 빈 문구가 「없음」 대신 이것이 된다 */
  chambersNotice?: string | undefined
  tray?: readonly TrayCell[] | undefined
  access?: ScreenAccess
  /**
   * 놓을 것들. `figureTabs(장비)` 가 낸 목록 그대로 넘긴다 — **「전체」는 여기서 뺀다.**
   * ⚠ 빈 목록이면(6축 로봇) 그 장비 그림 한 장만 선다.
   */
  tabs: readonly TabItem[]
  /** 구독이 끊겨 «지금»을 모른다 — 6축 동작 타일이 «마지막 값»을 붙인다(다른 장비 판은 종전 그대로) */
  stale?: boolean | undefined
  /**
   * 메트릭 카드(3축 로봇 · 본체 · cold rack)를 **판 밖 이 자리에** 세운다 — 모듈 앱의 영상 아래(eric 2026-10-18 「메트릭을... 원심분리기 화면
   * 아래에 넣어줘.. 통합 콘솔에는 적용하지 마」). 넘기면 그림 옆 상자가 없어지고 그림 카드가 판 폭을 다 쓴다. `null` 은 자리가 아직 안 섰다(아무 데도 안 그린다).
   * 넘기지 않으면(통합 콘솔) 종전대로 그림 옆이다. 6축은 언제나 그림 옆이다.
   */
  metricsTarget?: Element | null | undefined
  /** 통합 콘솔: 오른쪽 메트릭 아래에 같은 너비로 놓는 이벤트 카드. */
  events?: ReactNode
  /** 통합 콘솔: 현재 단계를 그림 아래 대신 처리 중 머리칸에 놓는다. */
  stepNoteInHeader?: boolean
  moduleSupplies?: SuppliesView | undefined
}

export function EquipmentSheet({
  deviceKey,
  modules,
  chambers,
  chambersNotice,
  tray,
  access = {},
  tabs,
  stale = false,
  metricsTarget,
  moduleSupplies,
  events,
  stepNoteInHeader = false,
}: EquipmentSheetProps) {
  const view = canView(access)
  const snapshot = findModule(modules, deviceKey)
  // 🔴 실행 여부는 **서버가 준 상태**다. 화면이 진행 중인지 판단하지 않는다
  const running = snapshot?.state === 'RUNNING'
  /* 판의 장비 이름 — 6축은 「운송 로봇」(eric 2026-10-13 「운송 로봇 모니터링 페이지에. 6축 로봇이라고 되어 있는데..」 · `monitoringDeviceName`) */
  const name = monitoringDeviceName(deviceKey)

  /*
   * 「전체」는 나머지 셋의 합이라 같이 놓으면 그림이 두 번 나온다.
   * 🔴 **부품 탭이 없는 장비(6축 로봇)는 제 그림 한 장이다.** 블록이 0개면 빈 화면이 된다.
   */
  const parts = tabs.filter((tab) => tab.id !== 'all')
  const blocks = parts.length > 0 ? parts : [{ id: deviceKey, label: name }]
  /*
   * 🔴 그림 격자에 영상 블록(`vision`)은 그리지 않는다 — 상세 카드의 비전 칸(2026-10-02).
   * 🔴 소모품 랙 블록(`SUPPLY_RACK_BLOCKS`)도 그리지 않는다 — 오른쪽 열 소모품 판과 같은 랙 그림이 겹친다(eric 2026-10-02).
   *    메트릭 패널에는 «가르기 전» `blocks` 를 그대로 넘긴다(`vision_verdict` · 시약 보관 온도는 `pip` 키로 선다).
   */
  const devices = equipmentBlocks(
    deviceKey,
    blocks.filter((block) => block.id !== 'vision' && !SUPPLY_RACK_BLOCKS.has(block.id)),
  )

  /*
   * 🔴 **염색기는 한 칸이다 — 트레이가 장치 정면도 «안»에 선다**(eric 2026-10-07 「트레이를 처리 중으로 넣고.. 트레이 L 트레이 후면 이거 없애..
   *    가운데에 L 트레이를 넣고」). 왼쪽 「트레이」 칸을 걷고 셰이커 블록(`shaker`) 하나가 「처리 중 염색기」 칸을 채운다(`StainingFrontLine`).
   *    이 이름 분기는 콘솔 장비 판 한 곳이다 — `equipmentBlocks()` · `FocusFigure` 의 다른 화면은 그대로다.
   */
  const stainFront = deviceKey === 'stain'
  const start = stainFront ? undefined : devices.find((block) => trayCorner(block.id) === 'start')
  const endTray = devices.find((block) => trayCorner(block.id) === 'end')
  /* 가운데 — **장비 본체(`mod`·`cent`)가 앞, 부속(TC 랙 등)이 뒤** */
  /* 6축(`robot`)도 본체다 — 단계 띠가 「6축 로봇」을 말하니 그림 위 이름표는 눈에서만 숨는다 */
  const BODY_FIRST = (block: { id: string }) =>
    block.id === 'mod' ||
    block.id === 'cent' ||
    block.id === 'robot' ||
    (stainFront && block.id === 'shaker')
      ? 0
      : 1
  const middleAll = devices
    .filter((block) => trayCorner(block.id) === undefined && !(stainFront && block.id === 'tray'))
    .toSorted((a, b) => BODY_FIRST(a) - BODY_FIRST(b))
  /* 끝 트레이가 없고 가운데가 둘 이상이면 마지막 블록이 셋째 칸에 선다. 염색기는 「피펫 · 시약」을 걷어 「트레이 | 셰이커」 두 칸이다 */
  const endBlock = endTray ?? (middleAll.length > 1 ? middleAll.at(-1) : undefined)
  /*
   * 🔴 **염색기 셋째 칸은 장치 정면도 한 장 — 피펫 헤드 + 구역 + 셰이커**(eric 2026-10-07 「어셈블리랑 염색기는 정면도가 나아보이는데..
   *    염색기를 그럼 정면도로 수정해줘」). 셰이커 블록(`shaker`)이 그 자리고 이름을 「피펫 · 셰이커」로 바꿔 단계 띠에서 읽힌다(`StainingFrontLine`).
   *    왼쪽 트레이 평면도는 그대로다.
   */
  const frontName = (block: { id: string; label: string }) =>
    stainFront && block.id === 'shaker'
      ? { ...block, label: t('monitoring.eqsheet.stainFront') }
      : block
  const end = endBlock === undefined ? undefined : frontName(endBlock)
  const middleBody = endTray === undefined && end !== undefined ? middleAll.slice(0, -1) : middleAll
  /*
   * 🔴 **어셈블러 가운데 칸은 장치 그림 한 장 — 압착(TC 장착) + UV 노출 정면도**(eric 2026-10-07 「압착 부분이랑 UV 노출 부분.. 두개..
   *    합쳐달라고 했잖아.. 위에서 바라 보는게 아니라 정면에서.. 압착(TC 장착 그림만 남기라고)」). 본체 블록(`mod`)이 그 자리고 이름을
   *    「압착 (TC 장착) · UV 노출」로 바꿔 그림 위에서 읽힌다(`AssemblerStationLine`). ⚠ 2026-10-06 의 평면도 두 칸(`press` + `mod`)은 걷었다.
   */
  const stations = deviceKey === 'asm'
  const middle = (
    stations
      ? middleBody.map((block) =>
          block.id === 'mod' ? { ...block, label: t('monitoring.eqsheet.station') } : block,
        )
      : middleBody
  ).map(frontName)
  /* 🔴 «지금»은 서버 모듈 상태 하나로만 — 끊긴 화면이면 켜지 않는다(`processSteps.ts`) */
  const quiet = stale || snapshot?.stale === true
  /* 어셈블러 장치 칸의 «지금» — TC 장착 → UV → TC 분리(서버 상태 하나) · 그림은 이 값이 바뀔 때 움직인다 */
  const asmStation = asmStationView(snapshot, quiet)
  const stationPhase = asmStation.phase
  const stationOn = stations && stationPhase !== 'idle'
  /* 염색기 정면도의 «지금» — 서버 시퀀스 · 상태 하나 · 아래 한 줄은 동작 이름 + 스텝(끊긴 화면이면 빈 글자) */
  /* 방금 끝난 동작 — 구독 스텝 줄(`step_complete` 의 `sequence`)에서 이 모듈의 맨 끝. 툴은 그 자리에 들렀다 간다(`stainFrontModel.ts`) */
  const completedAction = latestStepOf(
    useModuleStepLog(snapshot?.module_id),
    snapshot?.module_id,
  )?.action
  const frontPhase = stainFront ? stainFrontPhase(snapshot, quiet, completedAction) : 'idle'
  const frontArrow = stainFront ? stainFrontArrow(snapshot, quiet, completedAction) : undefined
  const frontWaste = stainFront ? stainFrontWaste(snapshot, quiet, completedAction) : undefined
  const frontStatus = stainFront && !quiet ? stainFrontStatus(snapshot, '') : ''
  const currentStep = currentProcessStep(deviceKey, snapshot?.state, quiet)
  /* 어셈블러 — 작업 전 트레이 아랫변과 TC 랙 · 폐기통 아랫변을 맞춘다(eric 2026-10-12 「트레이랑 어셈블러 라인이 안 맞아」) */
  const figsRef = useAlignDeckLine<HTMLDivElement>(deviceKey === 'asm')

  /* 공정 진행 줄이 서는 장비(지금은 6축만) — 카드 행이 셋, 아니면 둘(빈 띠를 남기지 않는다 · `monitoring.css` `--proc`) */
  const hasProc = PROCESS_STEPS[deviceKey] !== undefined
  const paired = endTray !== undefined
  /*
   * 🔴 **두 칸 장비(염색기 「트레이 | 셰이커」)는 한 배율로 선다**(eric 2026-10-02 「염색기 장비 화면이 대칭이 안 되는데...
   *    대칭이 되게」 → 「응 그렇게 해줘」). 칸은 1 : 1(`monitoring.css` 의 `--solo`)이고, 그림은 각 칸 혼자 맞춘 배율 중 «작은
   *    쪽»에 함께 선다 — 셰이커가 세로로 길어 그 칸이 먼저 차면 트레이도 같은 배율로 줄어 두 그림의 크기 기준이 같다.
   *    세 칸 장비(원심 · 어셈블러)는 종전대로 칸마다 따로다.
   */
  const solo = end === undefined && start !== undefined
  const [soloScales, setSoloScales] = useState<Readonly<Record<string, number>>>({})
  const soloValues = Object.values(soloScales)
  const soloCap = solo && soloValues.length >= 2 ? Math.min(...soloValues) : undefined
  const shareOf = (slot: 'pre' | 'dev'): FitZoomShare =>
    solo
      ? {
          cap: soloCap,
          onFit: (scale) =>
            setSoloScales((prev) =>
              prev[slot] !== undefined && Math.abs(prev[slot] - scale) < 0.01
                ? prev
                : { ...prev, [slot]: scale },
            ),
        }
      : {}
  /* 영상 아래 메트릭 — 가운데 칸 블록 중 `FIGURE_METRIC_BLOCKS` 에 든 것(원심분리기 `cent`). 패널 메트릭 구역에는 안 넘긴다 */
  const underMetrics = middle.filter((block) => FIGURE_METRIC_BLOCKS.has(block.id))
  /* 영상 아래 — 블록 메트릭 + 장비마다 3축 X · Y · Z · 끝장치 상태(`FIGURE_METRIC_EXTRA` · eric 2026-10-08) */
  const underSpec = [
    ...underMetrics.flatMap((block) => METRIC_SPECS[block.id] ?? []),
    ...(FIGURE_METRIC_EXTRA[deviceKey] ?? []),
  ]
  /*
   * 🔴 **메트릭 상자 — 장비 판 그림 영역 오른쪽 위**(eric 2026-10-12 「우측 상단에 사각형 형태로 메트릭 넣어줘.. 왼쪽에 지금 메트릭 내용은 지우고
   *    장비 내부 화면의 오른쪽 위에 위치시켜줘」). 종전 왼쪽 상세 카드의 두 무리(그림 메트릭 · 「현재 메트릭」 구역)를 한 상자로 합친다 —
   *    같은 칸 정의(`METRIC_SPECS` · `FIGURE_METRIC_EXTRA`)이고 같은 키는 한 번만. 소모품 잔량은 소모품 판이 말하니 뺀다(종전 그대로)
   */
  const boxSpec = [
    ...underSpec,
    /*
     * 종전 「현재 메트릭」 구역의 칸 — 🔴 그때처럼 «값(칸)이 온 것만» 선다(판정 · 슬라이더 · 셰이커처럼 서버가 아직 안 보내는 칸이 「미수신」으로
     *    줄줄이 서지 않게). 그림 메트릭(`underSpec`)은 종전대로 칸이 없어도 서고 「미수신」 + 사유다
     */
    ...blocks
      .filter((block) => !underMetrics.some((under) => under.id === block.id))
      .flatMap((block) => METRIC_SPECS[block.id] ?? [])
      .filter((item) => figureMetricTiles(snapshot, [item])[0]?.absent === false),
  ]
    .filter((item, index, all) => all.findIndex((other) => other.key === item.key) === index)
    .filter((item) => item.source !== 'consumables')

  /*
   * 단계 띠 — 시안 `.steps`. 쌍이 있으면 「01 작업 전 · 02 처리 중 · 03 작업 후」, 없으면(염색기) 번호 없이
   * 「트레이 · 처리 중 · 교반」. 이름은 우리 값이다(시안 글자를 박지 않는다).
   */
  const labels = TRAY_LABEL_KEY[deviceKey]
  const steps: { key: string; caption: string; name: string; current?: boolean }[] = [
    ...(start === undefined
      ? []
      : [
          {
            key: 'pre',
            caption: paired ? t('monitoring.legend.before') : t('figTab.tray'),
            name: t(labels.pre),
          },
        ]),
    { key: 'dev', caption: t('monitoring.eqsheet.processing'), name, current: true },
    ...(end === undefined
      ? []
      : [
          {
            key: 'post',
            caption: paired ? t('monitoring.legend.after') : t('monitoring.eqsheet.agitate'),
            name: paired ? t(labels.post) : end.label,
          },
        ]),
  ]
  /* 단계 띠가 이름을 말한 블록 — 그림 위 이름표는 눈에서만 숨긴다(시안 `.eq__cap--dup`) */
  const named = new Set(
    [
      start?.id,
      end?.id,
      /* 어셈블러 장치 그림도 이름을 눈에서 숨긴다(eric 2026-10-07 「압착 (TC 장착) · UV 노출 / 대기 — 이거 없애줘」) — 단계 띠가 「어셈블러」를 말한다 */
      ...middle.filter((block) => BODY_FIRST(block) === 0).map((b) => b.id),
    ].filter((id): id is string => id !== undefined),
  )

  const figure = (block: { id: string; label: string }) => (
    <section
      className={cx('figbox eqsheet__fig', named.has(block.id) && 'eqsheet__fig--named')}
      data-block={block.id}
      key={block.id}
    >
      {/* 🔴 이름은 DOM 에 늘 있다 — 단계 띠와 겹치면 `.eqsheet__fig--named` 가 눈에서만 숨긴다 */}
      <h3 className="figlbl">{block.label}</h3>
      {
        <FocusFigure
          /*
           * 어셈블러 본체 — 압착 + UV 정면도 한 장(`FocusFigure` 의 `station`). 압착 중 · 아래 한 줄은 서버 상태 이름만 —
           * 아닐 때는 비운다(「대기」 글자 줄은 걷었다 · eric 2026-10-07). UV · 스테이지 위 칸은 FocusFigure 의 재료 그대로
           */
          {...(stations && block.id === 'mod'
            ? {
                station: {
                  supplies: moduleSupplies,
                  phase: stationPhase,
                  down: asmStation.down,
                  holding: asmStation.holding,
                  /*
                   * 🔴 그림 안 그리퍼는 늘 끈다 — 장비 판에는 3축 그리퍼 그림이 없다(eric 2026-10-14 겹침 그리퍼도 걷음 · 아래 `FitCell` 뒤 주석).
                   *    툴 상태는 「3축 로봇」 카드의 서버 값이 말한다. 되켜면 서버 좌표 없이 짐작한 자리에 그리퍼가 선다
                   * 왼쪽 TC 랙은 모듈 소모품 남은 수로
                   */
                  gripper: false,
                  fitDevice: metricsTarget !== undefined,
                  tcRemaining: consumableNumber(snapshot, 'tc_remaining') ?? null,
                  statusLabel:
                    stationOn && snapshot?.state !== undefined
                      ? t(`enum.moduleState.${snapshot.state}` as MessageKey)
                      : '',
                },
              }
            : {})}
          {...(stainFront && block.id === 'shaker'
            ? {
                /* 그림 안 피펫 툴은 끈다 — 장비 판에는 3축 피펫 그림이 없다(eric 2026-10-14 겹침 피펫도 걷음). 팁 · 파지는 「3축 로봇」 카드가 말한다 */
                front: {
                  renderDeck:
                    moduleSupplies === undefined
                      ? undefined
                      : (plan: ReactNode, waste: ReactNode, now: StainDeckNow) => (
                          <ModuleSupplyStrip
                            supplies={moduleSupplies}
                            snapshot={snapshot}
                            plan={plan}
                            waste={waste}
                            now={now}
                          />
                        ),
                  phase: frontPhase,
                  statusLabel: frontStatus,
                  arrow: frontArrow,
                  waste: frontWaste,
                  tool: false,
                },
              }
            : {})}
          /* 6축 — 가로 라인(정거장 셋 + 레일 위 6축 · eric 2026-10-08 「가로로 줄 넣어주고 6축 로봇이 왔다갔다하는 것을」) */
          {...(deviceKey === 'robot' ? { robotLine: { modules, stale } } : {})}
          canViewPii={view}
          chambers={chambers}
          component={block.id}
          deviceKey={deviceKey}
          /*
           * 🔴 **트레이가 움직이는 근거가 이 한 줄이다**(eric 2026-09-22 「배치 동작함에 따라서 트레이의 상태가 바뀌어야」).
           *    `overview.stepActivity` 를 켜야 칸의 소재 · 로터 적재 · 강조가 산다. 이 컴포넌트는 통합 콘솔 모니터링과
           *    모듈 앱 3개(`createModuleMonitorRoutes` → `MonitoringContainer` `layout="deviceOnly"`)가 같이 그린다.
           */
          overview={STEP_ACTIVITY_ONLY}
          running={running}
          snapshot={snapshot}
          tray={tray}
          /* 트레이 이름표에 「작업 전 / 작업 후」 — 단계 띠와 같은 말(눈에서는 숨는다) */
          trayStage
          /* 🔴 한 줄 진행 문구는 그리지 않는다 — 작업 줄 3행이 말한다(eric 2026-09-22) */
          activityNote={false}
          /*
           * 🔴 블록 전체를 두르는 「지금 일하는 부품」 테 · 숨쉬기를 끈다(eric 2026-10-11 「전체가 다 깜빡거리니 이상하잖아」) —
           *    블록이 장비 한 장이라 테가 칸 전체였다. 단계 띠 「처리 중 ○○」와 그림 안 표지가 같은 말을 한다(`FocusFigure.blockHighlight`)
           */
          blockHighlight={false}
          /* 🔴 같은 사물은 시료 탭 그림으로 · 챔버는 먹색 평면 테두리(eric 2026-09-27 · 09-28 「장비에서는 3d 쓰지마」) */
          realFigures
          /* 그림 격자에 영상 블록이 없다 — 영상은 상세 카드의 비전 칸이다(2026-10-02) */
          visionFeed={undefined}
        />
      }
    </section>
  )

  /* 🔴 메트릭 카드를 판 밖(모듈 앱 영상 아래)에 세우는가 — 6축은 언제나 그림 옆이다 */
  const metricsAway = metricsTarget !== undefined && deviceKey !== 'robot'
  const metricBox = (
    <div
      aria-label={t('panel.detailTitle', { name })}
      className={cx(
        'eqsheet__metricbox eqsheet__metricbox--side eqsheet__metricbox--cards',
        events != null && 'eqsheet__metricbox--events',
      )}
      role="region"
    >
      {deviceKey === 'robot' ? (
        /* 6축 — 운송 로봇 카드(`RobotDetailTiles`) + 메트릭 구역. 정보 · 챔버 표 · 영상은 없다 · 핸드오프 자리 표는 걷었다(그림이 말한다 · eric 2026-10-21) */
        <MonitoringPanel
          canViewPii={view}
          chambers={chambers}
          chambersNotice={chambersNotice}
          component={blocks[0]?.id ?? deviceKey}
          components={blocks.map((block) => block.id)}
          deviceKey={deviceKey}
          lead={<RobotDetailTiles snapshot={snapshot} stale={stale} />}
          look="tiles"
          omitChambers
          omitConsumables
          omitInfo
          snapshot={snapshot}
        />
      ) : (
        /*
         * 원심 · 어셈블러 · 염색기 — 묶음마다 카드 하나(3축 로봇 · 본체 · cold rack · `FigureMetrics`). 🔴 「정보」(제어 소유 · 알람 · 응답 확인)는
         *    걷었다(eric 2026-10-12 「3축 로봇 cold rack 을 각각의 카드로 분리해주고. 정보 내용은 제거해줘」 · 「원심분리기 어셈블러 축 로봇도 동일하게」)
         */
        <FigureMetrics name={name} snapshot={snapshot} spec={boxSpec} compactState />
      )}
      {events}
    </div>
  )

  return (
    <div className={cx('eqsheet eqsheet--noside', events != null && 'eqsheet--events')}>
      {/*
        🔴 **왼쪽 상세 카드는 없다**(eric 2026-10-12 「여기에 정보는 그냥 빼줘」 → 「6축 로봇도 오른쪽 위에 메트릭 · 동작 · 위치 · 핸드오프 내용 옮겨줘」).
           영상은 비전 열 · 챔버 표는 시료 열 · 정보는 걷었고, 메트릭(6축은 동작 · 위치 · 핸드오프 자리까지)은 그림 카드 오른쪽 위 상자(`eqsheet__metricbox`)다.
           그림 카드가 판 전폭을 쓴다(`.eqsheet--noside`).
      */}
      {/*
        🔴 **오른쪽 열 — 장비 카드(단계 띠 + 그림 격자 · 6축은 + 공정 진행 줄) 하나**(eric 2026-10-06). 6축 로봇도 «같은 카드»다
           (「6축 로봇쪽도.. 염색기 어셈블러쪽과 같이.. 모양 일치 시켜줘」) — 단계 띠는 「처리 중 6축 로봇」 한 칸(출발 · 도착은
           서버가 안 줘 짓지 않는다), 그림 격자에 6축 팔 그림 한 장(`FocusFigure` → `RobotArm` · 다른 장비와 같은 `FitCell`),
           아래 공정 진행 줄(`MOVING → HANDOFF`). ⚠ 같은 날의 이송 그림(머리줄 · 레일 · 정거장 목록 — `RobotTransfer`)은 걷었다
           (eric 「여기서.. 다른 거 다 빼고... 6축 로봇 모양을 넣어줘」). 정거장 자리 찼음/빔은 왼쪽 상세(`RobotDetailTiles`)로 옮겼다.
      */}
      <div className="eqsheet__main">
        <section
          aria-label={t('monitoring.eqsheet.layout', { name })}
          className={cx(
            'eqsheet__card',
            hasProc && 'eqsheet__card--proc',
            /*
             * 그림 «옆» 오른쪽 열 — 6축만이던 배치를 전 장비로(eric 2026-10-12 「이거 6축 로봇 처럼.. 오른쪽에. 정보랑 메트릭 차지게 해줘」).
             * ⚠ 종전 원심 · 어셈블러 · 염색기는 단계 띠 아래 오른쪽 위 한 줄 상자였다 — 그림 위 빈 자리가 컸다
             */
            !metricsAway && 'eqsheet__card--sidebox',
          )}
        >
          <ol
            aria-label={t('monitoring.eqsheet.steps')}
            className={cx(
              'eqsheet__steps',
              end === undefined && start !== undefined && 'eqsheet__steps--solo',
              /* 칸 하나(6축 「처리 중 6축 로봇」) — 띠 폭을 다 쓴다 */
              steps.length === 1 && 'eqsheet__steps--one',
            )}
          >
            {steps.map((step, index) => (
              <li
                aria-current={step.current === true ? 'step' : undefined}
                className={cx('eqsheet__step', step.current === true && 'eqsheet__step--cur')}
                data-step={step.key}
                key={step.key}
              >
                {paired ? (
                  <span aria-hidden="true" className="eqsheet__stepno">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                ) : null}
                <span className="eqsheet__stepcap">{step.caption}</span>
                <b className="eqsheet__stepname">{step.name}</b>
                {stepNoteInHeader && step.current === true && deviceKey !== 'robot' ? (
                  <LatestStepNote
                    inHeader
                    moduleId={snapshot?.module_id}
                    name={name}
                    sequences={SEQUENCE_DICTIONARY[deviceKey]}
                    stale={quiet}
                  />
                ) : null}
              </li>
            ))}
          </ol>
          {/* 🔴 공정 진행 줄 — 단계 띠 «바로 아래»(eric 2026-10-07 「작업 전 처리 중 작업 후 아래에 놓을 수 있어?」). 서버 상태가 고른 칸 하나만 켠다.
              ⚠ 원심 · 어셈블러 · 염색기는 줄을 걷었다(eric 2026-10-07 「이것들 없애줘」) — 지금은 6축만 선다(`PROCESS_STEPS`) */}
          {/*
           * 정보 · 메트릭 상자 — 그림 «옆» 오른쪽 열(전 장비 · eric 2026-10-12 「6축 로봇 처럼.. 오른쪽에. 정보랑 메트릭 차지게 해줘」).
           *    🔴 늘 선다 — 메트릭 칸이 없어도 「정보」(제어 소유 · 알람 · 응답 확인)가 든다. 그림 위에 겹쳐 띄우지 않는다(제 열을 갖는다).
           *    ⚠ 종전(같은 날 오전) 「단계 띠 아래 오른쪽 위 한 줄 상자」는 걷었다 — 그림 위 빈 자리가 컸다
           */}
          {metricsAway
            ? metricsTarget == null
              ? null
              : createPortal(metricBox, metricsTarget)
            : metricBox}
          <ProcessSteps current={currentStep} device={deviceKey} name={name} />
          <div
            ref={figsRef}
            className={cx(
              'eqsheet__figs',
              end === undefined && start !== undefined && 'eqsheet__figs--solo',
              /* 칸 하나(6축 · 염색기) — 장치 그림 한 장이 칸 전체 가운데(칸 이름 `dev` 하나 · eric 2026-10-08 「트레이를 중간에 위치시켜」) */
              start === undefined && end === undefined && 'eqsheet__figs--one',
              /* 6축 — 팔 선 굵기 */
              deviceKey === 'robot' && 'eqsheet__figs--robot',
              /* 어셈블러 — 가운데 칸 채움 비(`monitoring.css` `.eqsheet__figs--asm`) */
              deviceKey === 'asm' && 'eqsheet__figs--asm',
            )}
          >
            {start === undefined ? null : (
              <FitCell share={shareOf('pre')} slot="pre">
                {figure(start)}
              </FitCell>
            )}
            <FitCell share={shareOf('dev')} slot="dev">
              {middle.length > 1 ? (
                /* 가운데 블록이 여럿인 장비 — 넓으면 나란히 · 좁으면 위아래(칸 모양 질의). ⚠ 어셈블러는 2026-10-07 부터 장치 그림 한 장이라 여기 안 온다 */
                <div className="eqsheet__stack">{middle.map(figure)}</div>
              ) : (
                middle.map(figure)
              )}
              {!stepNoteInHeader ? (
                <LatestStepNote
                  moduleId={snapshot?.module_id}
                  name={name}
                  sequences={SEQUENCE_DICTIONARY[deviceKey]}
                  stale={quiet}
                />
              ) : null}
            </FitCell>
            {end === undefined ? null : <FitCell slot="post">{figure(end)}</FitCell>}
            {/*
              🔴 **3축 그리퍼(원심 · 어셈블러) · 3축 피펫(염색기) 겹침 그림을 걷었다**(eric 2026-10-14 「실제로 실기 연동했을 때.. 걸리는 위치가..
                 드리퍼가 움직이는 중인데 해당 위에 가 있는 경우, 움직이는 속도를 모르니까.. 장비쪽 드리퍼랑 피펫 빼자」 → 「추천안대로 해봐」).
                 자리는 서버 좌표가 아니라 «지금 스텝»과 최근 분주 기록으로 짐작했고 이동 시간은 화면이 정한 애니메이션이었다 — 실장비에서는
                 그림이 실제 팔보다 앞서거나 뒤처진다. 툴 상태는 오른쪽 「3축 로봇」 카드(X · Y · Z · 그리퍼 파지 · 피펫 팁)의 서버 값이 말한다.
                 서버가 실제 좌표 · 「지금 처리 중인 웰」을 주면(`docs/backend-requests.csv`) 그때 다시 그린다 —
                 `CentGripper` · `AsmGripper` · `StainPipette` 파일은 남겨 두었다.
            */}
          </div>
        </section>
      </div>
    </div>
  )
}

/**
 * 그림 칸 하나 — 칸 크기에 그림을 맞춘다(`useFitZoom` · 칸마다 따로). 🔴 칸의 크기는 격자가 정하고(`container-type: size`)
 * 그림이 칸을 밀지 않는다. 글자는 `--fit-zoom` 으로 «÷» 되돌려 토큰 크기를 지킨다(`monitoring.css`).
 */
function FitCell({
  slot,
  share = {},
  children,
}: {
  slot: 'pre' | 'dev' | 'post'
  /** 여러 칸을 한 배율로 세울 때의 끈(`FitZoomShare`) — 안 넘기면 칸마다 따로 */
  share?: FitZoomShare
  children: ReactNode
}) {
  const [box, content] = useFitZoom<HTMLDivElement, HTMLDivElement>(share)
  return (
    <div className={`eqsheet__cell eqsheet__cell--${slot}`} data-slot={slot} ref={box}>
      <div className="eqsheet__fit" ref={content}>
        {children}
      </div>
    </div>
  )
}
